import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { sortBy } from 'lodash';

import {
  AtsInterviewDefinitionFragment,
  AtsType,
  useAtsInterviewDefinitionsByIdsQuery,
  useAtsInterviewDefinitionsQuery,
} from 'src/generated/mloop-graphql';

import { OrgPrefName, useOrgPrefString } from 'src/hooks/api/org';
import { useOrgAtsService } from 'src/hooks/atsService';
import { ScorecardGroupByStrategy } from 'src/hooks/atsService/AtsService';

import { getAshbyInterviewScopeSortIndex, getScorecardPrimarySortValue } from './scorecardGrouping';

export type AtsScorecardOption = AtsInterviewDefinitionFragment & { isDefault?: boolean };

export const AtsInterviewDefinitionsFragment = gql`
  fragment AtsInterviewDefinition on AtsInterviewDefinition {
    name
    atsId
    atsJobStageId
    atsJobId
    atsJobStage {
      atsId
      name
      index
    }
    atsFields {
      ... on GreenhouseInterviewDefinition {
        estimatedMinutes
        index
      }
      ... on LeverInterviewDefinition {
        groupId
        groupName
      }
    }
  }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const AtsInterviewDefinitionsQuery = gql`
  ${AtsInterviewDefinitionsFragment}
  query AtsInterviewDefinitions($input: AtsInterviewDefinitionsInput!) {
    atsInterviewDefinitions(input: $input) {
      atsInterviewDefinitions {
        ...AtsInterviewDefinition
      }
    }
  }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const AtsInterviewDefinitionsByIdsQuery = gql`
  ${AtsInterviewDefinitionsFragment}
  query AtsInterviewDefinitionsByIds($input: AtsInterviewDefinitionsByIdsInput!) {
    atsInterviewDefinitionsByIds(input: $input) {
      atsInterviewDefinitions {
        ...AtsInterviewDefinition
      }
    }
  }
`;

export const NoScorecardOption = {
  atsId: '',
} as AtsInterviewDefinitionFragment;

/**
 * Loads ATS interview definition options and orders them for the current ATS scorecard dropdown behavior.
 */
const useAtsScorecardOptions = (atsJobId?: string) => {
  const { t } = useTranslation();
  const atsService = useOrgAtsService();
  const [ashbyDefaultRemoteInterviewId] = useOrgPrefString(OrgPrefName.STR_ASHBY_DEFAULT_REMOTE_INTERVIEW_ID);

  const { data, loading, error } = useAtsInterviewDefinitionsQuery({
    skip: !atsJobId,
    variables: {
      input: {
        atsJobId: atsJobId || '',
        schedulable: true,
      },
    },
  });

  const sortByBy = atsService?.scorecardSortKey;
  const scorecardGroupByStrategy = atsService?.scorecardGroupByStrategy ?? ScorecardGroupByStrategy.AtsJobStage;
  const atsInterviewDefinitions = data?.atsInterviewDefinitions?.atsInterviewDefinitions || [];
  const isAshbyInterviewScope = scorecardGroupByStrategy === ScorecardGroupByStrategy.AshbyInterviewScope;
  let scorecards: AtsInterviewDefinitionFragment[] = [];

  if (isAshbyInterviewScope) {
    scorecards = sortBy(atsInterviewDefinitions, [
      getAshbyInterviewScopeSortIndex,
      (value) => value.name || null,
    ]).filter((template) => template.name);
  } else if (sortByBy) {
    scorecards = sortBy(atsInterviewDefinitions, (value) => getScorecardPrimarySortValue(value, sortByBy));
  } else {
    scorecards = sortBy(atsInterviewDefinitions, (value) => value.name || null).filter((template) => template.name);
  }

  const ashbyDefaultInterviewDefinition = ashbyDefaultRemoteInterviewId
    ? scorecards.find((scorecard) => scorecard.atsId === ashbyDefaultRemoteInterviewId)
    : undefined;
  const { data: ashbyDefaultInterviewDefinitionData } = useAtsInterviewDefinitionsByIdsQuery({
    skip: !isAshbyInterviewScope || !ashbyDefaultRemoteInterviewId || Boolean(ashbyDefaultInterviewDefinition),
    variables: {
      input: {
        atsType: AtsType.Ashby,
        atsInterviewDefinitionIds: ashbyDefaultRemoteInterviewId ? [ashbyDefaultRemoteInterviewId] : [],
      },
    },
  });
  const fetchedAshbyDefaultInterviewDefinition =
    ashbyDefaultInterviewDefinitionData?.atsInterviewDefinitionsByIds?.atsInterviewDefinitions?.[0];
  const defaultAshbyInterviewDefinition = ashbyDefaultInterviewDefinition || fetchedAshbyDefaultInterviewDefinition;
  const noScorecard: AtsScorecardOption = (
    isAshbyInterviewScope
      ? {
          ...NoScorecardOption,
          ...defaultAshbyInterviewDefinition,
          atsId: NoScorecardOption.atsId,
          isDefault: true,
          name: defaultAshbyInterviewDefinition?.name || t('hiring-dashboard-common:common.ats.ashbyDefaultInterview'),
        }
      : {
          ...NoScorecardOption,
          name: t(
            atsService?.noScorecardLabelTranslationKey || 'hiring-dashboard-common:common.ats.noScorecardFallback'
          ),
        }
  ) as AtsScorecardOption;

  return {
    loading,
    error,
    options: [noScorecard, ...scorecards.filter((scorecard) => scorecard.atsId !== noScorecard.atsId)],
  };
};

export default useAtsScorecardOptions;
